import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { isEmpty } from 'lodash';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Observable, of } from 'rxjs';
import { GenericValue } from '../../../core/model/generic-value';
import { HisOption } from '../../../genericsearch/model/his-option';
import { CommunicationLogUiText } from '../../communicationlog-uitexts';
import { CommunicationLogFilterSettings } from '../../model/communiacation-log-filter-settings';
import { CommunicationLogFilterValue } from '../communicationlog.component';

@Component( {
    selector: 'his-communication-log-filter',
    templateUrl: './communication-log-filter.component.html',
    styleUrl: './communication-log-filter.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class CommunicationLogFilterComponent implements OnChanges, OnInit {
    @Output() filterSettingsChanged: EventEmitter<CommunicationLogFilterSettings> = new EventEmitter<CommunicationLogFilterSettings>();
    @Input( { required: true } ) availableDomains: GenericValue[];
    @Input( { required: false } ) domainPreselection: number[];
    @Input( { required: true } ) filters: CommunicationLogFilterValue[];
    @Input() renderOwnerActivityFilter: boolean = false;

    protected showFilterOptions: boolean;
    protected filterSettings: CommunicationLogFilterSettings = new CommunicationLogFilterSettings();
    protected filteredDomains: GenericValue[];
    protected domainSelection: HisOption[] = [];
    protected uiText = CommunicationLogUiText;

    ngOnInit(): void {
        this.showFilterOptions = !isEmpty( this.domainPreselection );
    }

    ngOnChanges( changes: SimpleChanges ): void {
        if ( changes.availableDomains ) {
            this.applyDomainPreselection();
        }
    }

    private applyDomainPreselection(): void {
        if ( !isEmpty( this.domainPreselection ) ) {
            this.domainSelection = this.availableDomains.filter( domain => this.domainPreselection.includes( domain.id ) )
                .map( domain => this.convertToHisOption( domain ) );
        } else {
            this.domainSelection = this.availableDomains.map( domain => this.convertToHisOption( domain ) );
        }
        this.updateDomainSelection();
        this.emitFilterSettingsChanged();
    }

    protected toggleShowFilterOptions(): void {
        this.showFilterOptions = !this.showFilterOptions;
    }

    protected emitFilterSettingsChanged(): void {
        this.filterSettings.filters = this.filters.filter( filter => filter.value ).map( filter => filter.key );
        this.updateDomainSelection();
        this.filterSettingsChanged.emit( structuredClone( this.filterSettings ) );
    }

    protected filterDomains( event: AutoCompleteCompleteEvent ): void {
        const query = event.query;
        this.filteredDomains = this.availableDomains.filter( domain =>
            !this.domainSelection?.includes( this.convertToHisOption( domain ) ) &&
            domain.uniquename?.toLowerCase().includes( query.toLowerCase() )
        );
    }

    private updateDomainSelection(): void {
        this.filterSettings.domains = this.domainSelection.map( domain => parseInt( domain.id ) );
    }

    protected domainSelectionDisabled(): boolean {
        return this.domainPreselection?.length === 1 && !isEmpty( this.domainSelection );
    }

    protected customDomainOptions(): Observable<HisOption[]> {
        return of( this.availableDomains?.map( domain => ( this.convertToHisOption( domain ) ) ).sort( ( a, b ) => a.label.localeCompare( b.label ) ) );
    }

    private convertToHisOption( domain: GenericValue ): HisOption {
        return new HisOption( domain.longtext || domain.uniquename, `${domain.id}` );
    }
}
